import { type FC, useState } from "react";
import { type AddressButtonProps, type AddressDetails } from "./types";
import {
  saveAddress,
  type AddressData,
} from "../../../services/addresses";
import { getWorkerIdForUser, updateWorkerLocation } from "../../../services/workers";

interface HouseNumberButtonProps extends AddressButtonProps {
  offset: number;
  label: string;
}

export const HouseNumberButton: FC<HouseNumberButtonProps> = ({
  tempAddress,
  user,
  onAddressUpdate,
  offset,
  label,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    if (!tempAddress || !user?.id) return;

    // Extract number from house_number string
    const match = tempAddress.house_number.match(/(\d+)/);
    if (!match || !match[1]) return;

    const currentNumber = parseInt(match[1], 10);
    if (isNaN(currentNumber)) return;

    setIsLoading(true);
    try {
      // Create new address object with modified number
      const newAddress: AddressData = {
        street: tempAddress.street,
        house_number: (currentNumber + offset).toString(),
        zip_code: tempAddress.zip_code,
        city: tempAddress.city,
        country: tempAddress.country,
        latitude: tempAddress.latitude,
        longitude: tempAddress.longitude,
        created_by_user_id: user.id,
      };

      // Use saveAddress function to either get existing or create new
      const savedAddress = await saveAddress(newAddress);

      // Update tempAddress with saved address data
      if (savedAddress) {
        const updatedAddress: AddressDetails = {
          id: savedAddress.id,
          street: newAddress.street,
          house_number: newAddress.house_number,
          zip_code: newAddress.zip_code,
          city: newAddress.city,
          country: newAddress.country,
          latitude: newAddress.latitude,
          longitude: newAddress.longitude,
        };
        onAddressUpdate(updatedAddress);

        // Update worker location with new address coordinates
        try {
          const workerId = await getWorkerIdForUser(user.id);
          await updateWorkerLocation(
            workerId,
            newAddress.latitude.toString(),
            newAddress.longitude.toString()
          );
          console.log("Worker location updated successfully");
        } catch (error) {
          console.error("Error updating worker location:", error);
          // Don't show error to user since the main operation succeeded
        }
      }
    } catch (error) {
      console.error("Error handling house number change:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      className="rounded-lg bg-primary-100 px-2 py-1 text-sm font-medium text-primary-700 hover:bg-primary-200 dark:bg-primary-700 dark:text-white dark:hover:bg-primary-600"
      title={`Change house number by ${offset}`}
      disabled={isLoading || !user?.id}
    >
      {isLoading ? "..." : label}
    </button>
  );
};
